import { parseHmsString } from "../../components/utils/formatTime";
import { usesDepository } from "./types";
import { decimalToNumber, isPositiveDecimal } from "./amounts";
/** Displayed limits = backend limits + flat fee, floored by any provider minimum
 * (e.g. the Polymarket bridge's minimum checkout). The min is resolved dynamically
 * off the backend min, then raised to the provider floor when one applies. */
export function transformLimitsForExtendedRoute(limits, mapping) {
    if (!limits)
        return limits;
    const pricePerToken = limits.min_amount > 0 ? limits.min_amount_in_usd / limits.min_amount
        : limits.max_amount > 0 ? limits.max_amount_in_usd / limits.max_amount
            : 1;
    const min_amount = Math.max(limits.min_amount + mapping.flatFee, mapping.minAmount ?? 0);
    const max_amount = limits.max_amount + mapping.flatFee;
    return {
        min_amount,
        max_amount,
        min_amount_in_usd: min_amount * pricePerToken,
        max_amount_in_usd: max_amount * pricePerToken,
    };
}
/**
 * Re-denominate a backend quote so the source side reads as the extended
 * network/token: requested_amount = A, fee += flatFee, completion += extra time.
 */
export function transformQuoteForExtendedRoute(quote, mapping, extendedNetwork, extendedToken, sourceAmount) {
    if (!quote?.quote)
        return quote;
    const sourcePriceInUsd = extendedToken.price_in_usd || quote.quote.source_token?.price_in_usd || 0;
    return {
        ...quote,
        quote: {
            ...quote.quote,
            source_network: extendedNetwork,
            source_token: { ...extendedToken, price_in_usd: sourcePriceInUsd },
            requested_amount: decimalToNumber(sourceAmount),
            total_fee: quote.quote.total_fee + mapping.flatFee,
            total_fee_in_usd: quote.quote.total_fee_in_usd + mapping.flatFee * sourcePriceInUsd,
            blockchain_fee: quote.quote.blockchain_fee + mapping.flatFee,
            avg_completion_time: addSecondsToHms(quote.quote.avg_completion_time, mapping.extraCompletionSeconds),
        },
    };
}
function addSecondsToHms(value, addSeconds) {
    const parts = parseHmsString(value);
    const base = parts ? parts.hours * 3600 + parts.minutes * 60 + parts.seconds : 0;
    const total = base + addSeconds;
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
export function buildCreateSwapParamsForExtendedRoute({ plan, destinationNetworkName, destinationTokenSymbol, destinationAddress, referenceId, refuel, sourceAddress, }) {
    if (!plan.realAmount)
        throw new Error('Extended route amount is missing');
    if (!isPositiveDecimal(plan.realAmount))
        throw new Error('Extended route amount is invalid');
    const { provider } = plan.mapping;
    const depository = usesDepository(provider);
    return {
        amount: plan.realAmount,
        source_network: plan.mapping.real.networkName,
        source_token: plan.mapping.real.tokenSymbol,
        destination_network: destinationNetworkName,
        destination_token: destinationTokenSymbol,
        destination_address: destinationAddress,
        reference_id: referenceId,
        refuel: !!refuel,
        use_deposit_address: !depository,
        use_depository: depository || undefined,
        source_address: undefined,
        refund_address: provider.requiresRefundAddress ? sourceAddress : undefined,
    };
}
